/**
 * Relatorio de Turno (Folha de Registo de Producao Diaria).
 * Formato baseado no modelo EP3D com grelha horaria, paragens, MP, embalamento e resumo.
 */
const LOGO_EP3D = "https://customer-assets.emergentagent.com/job_89a536dc-36dd-4821-ba4f-a6c069ebdb60/artifacts/u7v2hasx_Logotipo.png";

const SHIFT_LABEL = { morning: "Manha", afternoon: "Tarde", night: "Noite" };

function pad(n) { return String(n).padStart(2, "0"); }

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function shiftHours(shift) {
  // Janelas horarias por turno (PT factory standard)
  if (shift === "morning") return [6, 7, 8, 9, 10, 11, 12, 13];
  if (shift === "afternoon") return [14, 15, 16, 17, 18, 19, 20, 21];
  if (shift === "night") return [22, 23, 0, 1, 2, 3, 4, 5];
  return [6, 7, 8, 9, 10, 11, 12, 13];
}

function groupRecordsByHour(records, shift) {
  const grid = {};
  shiftHours(shift).forEach(h => { grid[h] = { good: 0, rejected: 0, reasons: [], obs: [] }; });
  (records || []).forEach(r => {
    const d = new Date(r.recorded_at);
    const h = d.getHours();
    if (!grid[h]) grid[h] = { good: 0, rejected: 0, reasons: [], obs: [] };
    grid[h].good += r.good_quantity || 0;
    grid[h].rejected += r.rejected_quantity || 0;
    if (r.defect_observation) grid[h].reasons.push(r.defect_observation);
    else if (r.defect_type_id && r.rejected_quantity > 0) grid[h].reasons.push(r.defect_type_id);
  });
  return grid;
}

export function openShiftReport(data) {
  const shift = data.shift || "morning";
  const shiftLabel = SHIFT_LABEL[shift] || shift;
  const dateParts = (data.date || "").split("-");
  const dateStr = dateParts.length === 3 ? `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}` : data.date;

  const totalGood = data.totals?.good || 0;
  const totalRejected = data.totals?.rejected || 0;
  const totalProduced = totalGood + totalRejected;
  const rejectionRate = totalProduced > 0 ? (totalRejected / totalProduced * 100).toFixed(2) : "0.00";

  // Tempo total parado em minutos
  const totalStopMin = (data.stops || []).reduce((sum, s) => sum + (s.duration_minutes || 0), 0);

  // Eficiencia: bom / (bom + rejeitado + perdas paragens convertidas em pecas)
  // Aproximacao: bom / target * 100
  const target = data.order?.good_quantity_target || 0;
  const efficiency = target > 0 ? (totalGood / target * 100).toFixed(1) : "-";

  const opNames = (data.operators || []).map(o => `${o.code} - ${o.name}`).join(" / ") || "______________________";

  // Grelha de producao horaria
  const grid = groupRecordsByHour(data.production_records || [], shift);
  let acc = 0;
  const cycleTime = data.mold?.cycle_time || "";
  const hourRows = shiftHours(shift).map(h => {
    const row = grid[h] || { good: 0, rejected: 0, reasons: [], obs: [] };
    acc += row.good;
    return `<tr>
      <td class="bold">${pad(h)}:00</td>
      <td class="num">${cycleTime || ""}</td>
      <td class="num">${row.good || ""}</td>
      <td class="num">${row.good ? acc : ""}</td>
      <td class="num">${row.rejected || ""}</td>
      <td>${[...new Set(row.reasons)].join(", ")}</td>
      <td>${[...new Set(row.obs)].join(", ")}</td>
    </tr>`;
  }).join("");

  // Paragens
  const stopsRows = (data.stops || []).map(s => {
    return `<tr>
      <td>${fmtTime(s.start_time)}</td>
      <td>${fmtTime(s.end_time)}</td>
      <td class="num">${s.duration_minutes ? s.duration_minutes + " min" : "(em curso)"}</td>
      <td>${s.reason || s.stop_reason_id || "-"}</td>
    </tr>`;
  }).join("") || `<tr><td colspan="4" class="empty">Sem paragens registadas</td></tr>`;

  // Caixas/embalamento
  const boxes = data.box_labels || [];
  const sacosTeoricos = data.part?.units_per_bag ? (totalGood / data.part.units_per_bag).toFixed(1) : "0";
  const caixasCompletas = boxes.length;
  const pecasEmbaladas = boxes.reduce((s, b) => s + (b.quantity || 0), 0);
  const pecasStock = Math.max(0, totalGood - pecasEmbaladas);

  // Checklist arranque
  const cl = data.checklist;

  const html = `<!DOCTYPE html>
<html lang="pt">
<head>
<meta charset="utf-8" />
<title>Folha Registo Producao - ${data.order.code} - ${dateStr}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; font-size: 10.5px; padding: 12mm 10mm; }
  h1 { font-size: 18px; color: #1e40af; text-transform: uppercase; letter-spacing: 1px; }
  h2 { background: #0f172a; color: #fff; padding: 5px 10px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.7px; border-left: 4px solid #facc15; margin-top: 10px; }
  .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 4px solid #1e40af; padding-bottom: 7px; margin-bottom: 10px; }
  .brand-name { font-size: 16px; font-weight: 900; color: #0f172a; letter-spacing: -0.3px; }
  .brand-tag { font-size: 9px; color: #1e40af; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0; border: 1px solid #cbd5e1; }
  .meta-grid div { padding: 4px 8px; border-bottom: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; font-size: 10.5px; }
  .meta-grid .lbl { color: #64748b; font-weight: 700; text-transform: uppercase; font-size: 8.5px; }
  table { width: 100%; border-collapse: collapse; margin-top: 3px; font-size: 10px; }
  th, td { padding: 4px 6px; border: 1px solid #cbd5e1; text-align: left; }
  th { background: #f1f5f9; font-size: 9px; font-weight: 700; text-transform: uppercase; color: #475569; }
  td.num { text-align: right; font-family: 'Courier New', monospace; font-weight: 600; }
  td.bold { font-weight: 700; }
  .totals { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; margin: 6px 0; }
  .totals .card { background: #0f172a; color: #fff; padding: 6px 8px; border-radius: 3px; text-align: center; }
  .totals .card .lbl { font-size: 8px; color: #f97316; text-transform: uppercase; font-weight: 700; }
  .totals .card .val { font-size: 14px; font-weight: 800; }
  .sign-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 14px; font-size: 10px; }
  .sign { border-top: 1px solid #0f172a; padding-top: 3px; font-weight: 600; }
  .foot { margin-top: 12px; text-align: center; font-size: 9px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 4px; }
  .empty { color: #94a3b8; font-style: italic; text-align: center; }
  .check-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; font-size: 10px; padding: 6px 0; }
  .check-grid label { display: flex; gap: 6px; align-items: center; }
  .check-grid input { transform: scale(1.1); }
  .obs-box { border: 1px solid #cbd5e1; min-height: 32px; padding: 4px 8px; font-size: 10px; }
  .print-btn { position: fixed; top: 10px; right: 10px; background: #f97316; color: #fff; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-weight: 700; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none; } }
</style>
</head>
<body>
<button class="print-btn no-print" onclick="window.print()">🖨️ Imprimir</button>

<div class="header">
  <div style="display:flex; align-items:center; gap:10px;">
    <img src="${LOGO_EP3D}" alt="EP3D" style="height:48px;" />
    <div>
      <div class="brand-name">ENGINEERING PROJECT 3D</div>
      <div class="brand-tag">Engenharia · Plásticos · Qualidade</div>
    </div>
  </div>
  <div style="text-align:right">
    <h1>Folha de Registo</h1>
    <div style="font-size: 11px; color: #475569; font-weight: 600;">Producao Diaria</div>
  </div>
</div>

<div style="display:flex; justify-content:space-between; gap:10px; margin-bottom:6px; font-size:11px;">
  <div><strong>${data.part?.code || ""}</strong> — ${data.part?.description || ""}</div>
  <div style="text-align:right">Data: <strong>${dateStr}</strong> · Turno: <strong>${shiftLabel}</strong></div>
</div>

<div class="meta-grid">
  <div><div class="lbl">Maquina</div><div>${data.machine?.code || "-"}</div></div>
  <div><div class="lbl">Molde</div><div>${data.mold?.code || "-"} (${data.mold?.cavities || "?"} cavidades)</div></div>
  <div><div class="lbl">Material</div><div>${data.raw_material?.plastic_type || data.part?.plastic_type || "-"}</div></div>
  <div><div class="lbl">Operador(es)</div><div>${opNames}</div></div>
  <div><div class="lbl">OF Nº</div><div>${data.order?.code || "-"}</div></div>
  <div><div class="lbl">Lote MP</div><div>${data.raw_material?.lot_number || "______________"}</div></div>
</div>

<h2>1. Dados da Producao (Horario)</h2>
<table>
  <thead><tr>
    <th style="width:9%">Hora</th>
    <th style="width:9%">Ciclo (s)</th>
    <th style="width:13%">Produção</th>
    <th style="width:13%">Acumulada</th>
    <th style="width:11%">Rejeições</th>
    <th>Motivo</th>
    <th>Observacoes</th>
  </tr></thead>
  <tbody>${hourRows}</tbody>
</table>

<h2>2. Paragens de Producao</h2>
<table>
  <thead><tr>
    <th style="width:15%">Hora Inicio</th>
    <th style="width:15%">Hora Fim</th>
    <th style="width:15%">Duracao</th>
    <th>Motivo</th>
  </tr></thead>
  <tbody>${stopsRows}</tbody>
</table>

<h2>3. Embalamento</h2>
<table>
  <thead><tr><th style="width:60%">Descricao</th><th>Quantidade</th></tr></thead>
  <tbody>
    <tr><td>Sacos produzidos (${data.part?.units_per_bag || 250} un.)</td><td class="num">${sacosTeoricos}</td></tr>
    <tr><td>Caixas completas (${data.part?.bags_per_box || 12} sacos)</td><td class="num">${caixasCompletas}</td></tr>
    <tr><td>Pecas embaladas</td><td class="num">${pecasEmbaladas.toLocaleString("pt-PT")}</td></tr>
    <tr><td>Pecas em stock intermedio</td><td class="num">${pecasStock.toLocaleString("pt-PT")}</td></tr>
  </tbody>
</table>

${cl ? `
<h2>4. Checklist Arranque</h2>
<div style="font-size:10px; padding: 3px 0;">
  Peso 10 pecas medido: <strong>${cl.weight_10_pieces ? cl.weight_10_pieces.toFixed(2) + " g" : "______ g"}</strong>
  &nbsp;·&nbsp; Aprovado por: <strong>${cl.approved_by || "______________"}</strong>
  &nbsp;·&nbsp; Hora arranque: <strong>${fmtTime(cl.startup_time) || "__:__"}</strong>
  &nbsp;·&nbsp; Estado: <strong style="color:${cl.all_passed ? '#16a34a' : '#dc2626'}">${cl.all_passed ? "APROVADO" : "NAO APROVADO"}</strong>
</div>
` : ""}

<h2>5. Resumo do Turno</h2>
<div class="totals">
  <div class="card"><div class="lbl">Producao Total</div><div class="val">${totalGood.toLocaleString("pt-PT")}</div></div>
  <div class="card"><div class="lbl">Rejeicoes</div><div class="val">${totalRejected.toLocaleString("pt-PT")}</div></div>
  <div class="card"><div class="lbl">Taxa Rejeicao</div><div class="val">${rejectionRate}%</div></div>
  <div class="card"><div class="lbl">Eficiencia</div><div class="val">${efficiency}${typeof efficiency === "string" && efficiency !== "-" ? "%" : ""}</div></div>
  <div class="card"><div class="lbl">Tempo Paragem</div><div class="val">${totalStopMin} min</div></div>
</div>

<h2>6. Motivos de Rejeicao</h2>
<div class="check-grid">
  <label><input type="checkbox" /> Chupados</label>
  <label><input type="checkbox" /> Rebarbas</label>
  <label><input type="checkbox" /> Falta de enchimento</label>
  <label><input type="checkbox" /> Deformacao</label>
  <label><input type="checkbox" /> Alteracao de cor</label>
  <label><input type="checkbox" /> Quebra da haste</label>
  <label><input type="checkbox" /> Contaminacao</label>
  <label><input type="checkbox" /> Outro: ___________</label>
</div>

<h2>7. Observacoes</h2>
<div class="obs-box">&nbsp;</div>
<div class="obs-box" style="margin-top:2px">&nbsp;</div>

<div class="sign-row">
  <div class="sign">Operador<br/><span style="font-weight:400">${opNames}</span></div>
  <div class="sign">Responsavel de Producao<br/><span style="font-weight:400">______________________</span></div>
  <div class="sign">Responsavel da Qualidade<br/><span style="font-weight:400">______________________</span></div>
</div>

<div class="foot">
  Documento interno EP3D - Engineering Project 3D, Unipessoal Lda &nbsp;·&nbsp; Revisao 01
</div>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) { alert("Bloqueado pelo browser. Permita pop-ups."); return; }
  w.document.write(html);
  w.document.close();
}
